import { chooseRanked } from "../game/rankedChoice.ts";
import type { SearchInfo } from "./stockfish.ts";

const MAX_OPENING_CANDIDATES = 5;

/** Include both chessops/Chess960-style and standard-UCI castling encodings. */
export function theoryMoveUcis(moves: readonly { uci: string; san: string }[]): Set<string> {
  const ucis = new Set<string>();
  for (const move of moves) {
    ucis.add(move.uci);
    const rank = move.uci[1];
    const orig = move.uci.slice(0, 2);
    if (move.san.startsWith("O-O-O")) {
      ucis.add(`${orig}a${rank}`);
      ucis.add(`${orig}c${rank}`);
    } else if (move.san.startsWith("O-O")) {
      ucis.add(`${orig}g${rank}`);
      ucis.add(`${orig}h${rank}`);
    }
  }
  return ucis;
}

/** Stockfish-ranked first moves that also occur in the local theory graph. */
export function rankedTheoryMoves(
  infos: readonly SearchInfo[],
  theoryMoves: ReadonlySet<string>,
): string[] {
  const seen = new Set<string>();
  return [...infos]
    .sort((a, b) => (a.multipv ?? 1) - (b.multipv ?? 1))
    .map((info) => info.pv[0])
    .filter((uci): uci is string => {
      if (!uci || !theoryMoves.has(uci) || seen.has(uci)) return false;
      seen.add(uci);
      return true;
    })
    .slice(0, MAX_OPENING_CANDIDATES);
}

/**
 * Return a varied theory reply only when at least two engine-approved branches
 * exist. Otherwise the caller keeps Stockfish's ordinary bestmove.
 */
export function chooseOpeningReply(
  infos: readonly SearchInfo[],
  theoryMoves: ReadonlySet<string>,
  rng: () => number = Math.random,
): string | undefined {
  const ranked = rankedTheoryMoves(infos, theoryMoves);
  return ranked.length > 1 ? chooseRanked(ranked, rng) : undefined;
}
